import type { IDisposable } from './disposable';

export function disposify(dispose: (() => void) | undefined): IDisposable {
    return {
        dispose: () => {
            if (dispose) {
                dispose();
                dispose = undefined;
            }
        },
    };
}

export function setAwaitableTimeout(ms?: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function setDisposableTimeout(callback: () => unknown, ms: number): IDisposable {
    const timeout = setTimeout(callback, ms);

    return disposify(() => clearTimeout(timeout));
}

export function setDisposableInterval(callback: () => unknown, ms: number): IDisposable {
    const interval = setInterval(callback, ms);

    return disposify(() => clearInterval(interval));
}

export function addDisposableEventListener(
    element: EventTarget,
    event: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
): IDisposable {
    element.addEventListener(event, listener, options);

    return disposify(() => element.removeEventListener(event, listener, options));
}

export type ReadonlyRecord<K extends PropertyKey, V> = Readonly<Record<K, V>>;

export type Writable<T> = {
    -readonly [P in keyof T]: T[P];
};

export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export type Nullable<T> = T | null;
